import { Link } from 'react-router-dom';
import { BUSINESS_INFO } from '../../mocks/lisaMerkleData';

const PAGE_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services' },
  { label: 'About Us', path: '/about' },
  { label: 'Location', path: '/location' },
];

const SERVICE_LINKS = [
  { label: 'Full House Design', path: '/services' },
  { label: 'Renovation Consultation', path: '/services' },
  { label: 'Kitchen Design', path: '/services' },
  { label: 'Bathroom Design', path: '/services' },
  { label: 'Bedroom Design', path: '/services' },
  { label: 'Living Room Design', path: '/services' },
  { label: 'Space Planning & Layout', path: '/services' },
  { label: 'Art Selection & Procurement', path: '/services' },
];

export default function Footer() {
  return (
    <footer style={{ background: '#0F0F0F' }}>
      {/* Map Strip */}
      <div className="w-full h-[280px] overflow-hidden relative">
        <div className="absolute inset-0 bg-charcoal/30 z-10 pointer-events-none" />
        <iframe
          src={BUSINESS_INFO.mapEmbed}
          width="100%"
          height="280"
          style={{ border: 0, filter: 'grayscale(100%) contrast(0.85) brightness(0.9)' }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Lisa Merkle Design — Beverly Hills Location"
          className="w-full h-full"
        />
      </div>

      {/* Top border accent */}
      <div className="w-full h-px bg-gradient-to-r from-transparent via-stone/20 to-transparent" />

      {/* Main Footer */}
      <div className="max-w-[1400px] mx-auto px-8 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 pb-14 border-b border-white/5">

          {/* Brand Column */}
          <div className="md:col-span-4">
            <Link to="/" className="inline-block mb-6 cursor-pointer">
              <img
                src={BUSINESS_INFO.logoUrl}
                alt="Lisa Merkle Design"
                className="h-10 w-auto object-contain brightness-0 invert opacity-85"
              />
            </Link>
            <p className="text-white/40 text-sm leading-relaxed mb-6 max-w-[260px] font-sans font-light">
              {BUSINESS_INFO.tagline}
            </p>
            <div className="flex items-center gap-3 mb-6">
              <a
                href={BUSINESS_INFO.instagram}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-9 h-9 flex items-center justify-center rounded-full border border-white/10 text-white/35 hover:text-stone hover:border-stone/40 transition-all cursor-pointer"
                aria-label="Instagram"
              >
                <i className="ri-instagram-line text-sm" />
              </a>
              <a
                href={BUSINESS_INFO.facebook}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-9 h-9 flex items-center justify-center rounded-full border border-white/10 text-white/35 hover:text-stone hover:border-stone/40 transition-all cursor-pointer"
                aria-label="Facebook"
              >
                <i className="ri-facebook-fill text-sm" />
              </a>
            </div>
            {/* Publications */}
            <div className="flex items-center gap-3">
              <span className="text-white/20 text-[10px] tracking-[0.2em] uppercase font-sans">In</span>
              <span className="text-stone/60 font-display italic text-sm">Elle Décor</span>
              <div className="w-px h-3 bg-white/10" />
              <span className="text-stone/60 font-display italic text-sm">Arch. Digest</span>
            </div>
          </div>

          {/* Nav Column */}
          <div className="md:col-span-2">
            <h4 className="text-white/20 text-[10px] tracking-[0.25em] uppercase mb-6 font-sans">Navigation</h4>
            <ul className="flex flex-col gap-3">
              {PAGE_LINKS.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/50 text-sm hover:text-white/80 transition-colors cursor-pointer font-sans font-light"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Column */}
          <div className="md:col-span-3">
            <h4 className="text-white/20 text-[10px] tracking-[0.25em] uppercase mb-6 font-sans">Services</h4>
            <ul className="flex flex-col gap-3">
              {SERVICE_LINKS.map((service) => (
                <li key={service.label}>
                  <Link
                    to={service.path}
                    className="text-white/50 text-sm hover:text-white/80 transition-colors cursor-pointer font-sans font-light"
                  >
                    {service.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div className="md:col-span-3">
            <h4 className="text-white/20 text-[10px] tracking-[0.25em] uppercase mb-6 font-sans">Contact</h4>
            <ul className="flex flex-col gap-5">
              <li>
                <a
                  href={BUSINESS_INFO.phoneHref}
                  className="flex items-start gap-3 group cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <i className="ri-phone-line text-stone/60 group-hover:text-stone transition-colors text-sm" />
                  </span>
                  <div>
                    <p className="text-white/25 text-[10px] tracking-wider uppercase font-sans">Phone</p>
                    <p className="text-white/60 group-hover:text-white/80 transition-colors text-sm font-sans mt-0.5">{BUSINESS_INFO.phone}</p>
                  </div>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${BUSINESS_INFO.email}`}
                  className="flex items-start gap-3 group cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <i className="ri-mail-line text-stone/60 group-hover:text-stone transition-colors text-sm" />
                  </span>
                  <div>
                    <p className="text-white/25 text-[10px] tracking-wider uppercase font-sans">Email</p>
                    <p className="text-white/60 group-hover:text-white/80 transition-colors text-sm font-sans mt-0.5">{BUSINESS_INFO.email}</p>
                  </div>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <i className="ri-map-pin-line text-stone/60 text-sm" />
                  </span>
                  <div>
                    <p className="text-white/25 text-[10px] tracking-wider uppercase font-sans">Studio</p>
                    <p className="text-white/60 text-sm font-sans mt-0.5 leading-relaxed">
                      {BUSINESS_INFO.address}<br />
                      {BUSINESS_INFO.city}
                    </p>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/20 text-xs font-sans font-light">
            &copy; {new Date().getFullYear()} Lisa Merkle Design. All Rights Reserved.
          </p>
          <div className="flex items-center gap-4">
            <span className="text-white/15 text-[10px] font-sans tracking-wider">Beverly Hills Interior Design</span>
            <div className="w-px h-3 bg-white/10" />
            <span className="text-white/15 text-[10px] font-sans tracking-wider">Los Angeles</span>
            <div className="w-px h-3 bg-white/10" />
            <span className="text-white/15 text-[10px] font-sans tracking-wider">California</span>
            <div className="w-px h-3 bg-white/10" />
            <span className="text-white/15 text-[10px] font-sans tracking-wider">Nationwide</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
